// Simple country-based mapping using countries.json
import countriesData from '../../../cloudmanagement/countries.json';

// Fallback mapping for old AWS-style region codes to country codes
export const LEGACY_REGION_MAPPING = {
  'us-east-1': 'USA',
  'us-east-2': 'USA',
  'us-west-1': 'USA',
  'us-west-2': 'USA',
  'us-central-1': 'USA',
  'us-south-1': 'USA',
  'eu-west-1': 'IRL',
  'eu-west-2': 'GBR',
  'eu-west-3': 'FRA',
  'eu-central-1': 'DEU',
  'eu-north-1': 'SWE',
  'eu-south-1': 'ITA',
  'ap-south-1': 'IND',
  'ap-southeast-1': 'SGP',
  'ap-southeast-2': 'AUS',
  'ap-northeast-1': 'JPN',
  'ap-northeast-2': 'KOR',
  'ap-northeast-3': 'JPN',
  'ap-east-1': 'HKG',
  'ca-central-1': 'CAN',
  'sa-east-1': 'BRA',
  'me-south-1': 'BHR',
  'af-south-1': 'ZAF',
  'cn-north-1': 'CHN',
  'cn-northwest-1': 'CHN'
};

// Create a mapping from country codes to country objects
export const COUNTRY_MAPPING = countriesData.reduce((acc, country) => {
  acc[country.key] = {
    name: country.name,
    isoCode: country.key,
    latitude: country.latitude,
    longitude: country.longitude
  };
  return acc;
}, {});

// For backward compatibility, merge legacy regions with country mapping
export const REGION_MAPPING = {
  ...COUNTRY_MAPPING,
  ...LEGACY_REGION_MAPPING
};

// Get coordinates for a region
export const getRegionCoordinates = (region) => {
  const regionData = REGION_MAPPING[region];
  if (regionData) {
    return [regionData.longitude, regionData.latitude];
  }

  return [0, 0]; // Final fallback
};

// Get region info including display name and location
export const getRegionInfo = (region) => {
  // First try to get the region directly
  let regionData = REGION_MAPPING[region];

  // If it's a legacy region code, get the country info
  if (!regionData && LEGACY_REGION_MAPPING[region]) {
    const countryCode = LEGACY_REGION_MAPPING[region];
    regionData = COUNTRY_MAPPING[countryCode];
  }

  return regionData || {
    name: region || 'Unknown',
    isoCode: region || 'UNK',
    latitude: 0,
    longitude: 0
  };
};

// Get all available countries for forms (exclude legacy region codes)
export const getAvailableRegions = () => {
  return Object.keys(COUNTRY_MAPPING).map(key => ({
    value: key,
    label: COUNTRY_MAPPING[key].name,
    country: COUNTRY_MAPPING[key].name,
    isoCode: COUNTRY_MAPPING[key].isoCode
  }));
};

// ISO 3-letter to 2-letter country code mapping for map compatibility
export const ISO3_TO_ISO2 = {
  'USA': 'US',
  'CAN': 'CA',
  'MEX': 'MX',
  'BRA': 'BR',
  'ARG': 'AR',
  'CHL': 'CL',
  'COL': 'CO',
  'PER': 'PE',
  'URY': 'UY',
  'PRY': 'PY',
  'ECU': 'EC',
  'BOL': 'BO',
  'GBR': 'GB',
  'FRA': 'FR',
  'DEU': 'DE',
  'ITA': 'IT',
  'ESP': 'ES',
  'NLD': 'NL',
  'BEL': 'BE',
  'CHE': 'CH',
  'AUT': 'AT',
  'SWE': 'SE',
  'NOR': 'NO',
  'DNK': 'DK',
  'FIN': 'FI',
  'POL': 'PL',
  'CZE': 'CZ',
  'HUN': 'HU',
  'PRT': 'PT',
  'GRC': 'GR',
  'IRL': 'IE',
  'JPN': 'JP',
  'CHN': 'CN',
  'KOR': 'KR',
  'IND': 'IN',
  'SGP': 'SG',
  'THA': 'TH',
  'MYS': 'MY',
  'IDN': 'ID',
  'PHL': 'PH',
  'VNM': 'VN',
  'AUS': 'AU',
  'NZL': 'NZ',
  'PAK': 'PK',
  'BGD': 'BD',
  'SAU': 'SA',
  'ARE': 'AE',
  'ISR': 'IL',
  'TUR': 'TR',
  'EGY': 'EG',
  'ZAF': 'ZA',
  'NGA': 'NG',
  'KEN': 'KE',
  'MAR': 'MA',
  'TUN': 'TN',
  'JOR': 'JO',
  'LBN': 'LB',
  'IRN': 'IR',
  'IRQ': 'IQ',
  'QAT': 'QA',
  'KWT': 'KW',
  'HKG': 'HK',
  'BHR': 'BH'
};

// Get country code for map visualization (returns 2-letter ISO code)
export const getCountryCodeForRegion = (region) => {
  // Check if it's a legacy region code first
  if (LEGACY_REGION_MAPPING[region]) {
    const iso3Code = LEGACY_REGION_MAPPING[region];
    return ISO3_TO_ISO2[iso3Code] || iso3Code;
  }

  const regionData = REGION_MAPPING[region];
  if (regionData) {
    return ISO3_TO_ISO2[regionData.isoCode] || regionData.isoCode;
  }

  return region;
};

// Enhanced node enhancement with geo data
export const enhanceNodeWithLocationData = (node) => {
  const regionInfo = getRegionInfo(node.region);
  const coordinates = getRegionCoordinates(node.region);
  const mapLocationCode = getCountryCodeForRegion(node.region);

  return {
    ...node,
    coordinates,
    location: mapLocationCode, // Use 2-letter code for map compatibility
    locationName: regionInfo.name,
    country: regionInfo.name,
    latitude: regionInfo.latitude,
    longitude: regionInfo.longitude
  };
};



// Get nodes grouped by region for statistics
export const groupNodesByRegion = (nodes) => {
  const grouped = {};

  nodes.forEach(node => {
    const region = node.region || 'unknown';
    if (!grouped[region]) {
      grouped[region] = {
        nodes: [],
        regionInfo: getRegionInfo(region),
        count: 0
      };
    }
    grouped[region].nodes.push(node);
    grouped[region].count++;
  });

  return grouped;
};

// Calculate region statistics
export const calculateRegionStats = (nodes) => {
  const regionGroups = groupNodesByRegion(nodes);
  const stats = {};

  Object.keys(regionGroups).forEach(region => {
    const group = regionGroups[region];
    stats[region] = {
      count: group.count,
      active: group.nodes.filter(n => n.status === 'Active').length,
      inactive: group.nodes.filter(n => n.status !== 'Active').length,
      regionInfo: group.regionInfo
    };
  });

  return stats;
};
